'use client';

import React, { useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { ARTICULOS_AYUDA } from '@/content/ayuda/articulos';
import { faqs, FAQCategory } from '@/content/faqs';
import { Search, ChevronDown, ChevronUp, HelpCircle, Mail } from 'lucide-react';
import ListaArticulosAyuda from './ListaArticulosAyuda';
import { useFaqDesdeHash } from './useFaqDesdeHash';

// Las guías de uso son los artículos que no vienen de una pregunta frecuente: las de las preguntas ya están
// en el acordeón de abajo y cada una enlaza a su propia página.
const GUIAS_DE_USO = ARTICULOS_AYUDA.filter((articulo) => !articulo.faq);
const IDS_FAQ = faqs.map((faq) => faq.id);
const SLUG_POR_ID_FAQ: ReadonlyMap<string, string> = new Map(
    ARTICULOS_AYUDA.flatMap((articulo) => (articulo.faq ? [[articulo.faq.id, articulo.slug] as const] : [])),
);

const ETIQUETAS_CATEGORIA: Record<FAQCategory, string> = {
    [FAQCategory.SETUP]: '🚀 Primeros Pasos',
    [FAQCategory.SECURITY]: '🔒 Seguridad',
    [FAQCategory.PRICING]: '💳 Planes',
    [FAQCategory.CLINICAL]: '🧠 Uso Clínico',
    [FAQCategory.TECHNICAL]: '🔧 Problemas Técnicos',
};

export const HelpCenterSection = () => {
    const [terminoBusqueda, setTerminoBusqueda] = useState('');
    const [faqAbierta, setFaqAbierta] = useState<string | null>(null);
    const [categoriaActiva, setCategoriaActiva] = useState<FAQCategory | 'TODAS'>('TODAS');

    const faqsFiltradas = useMemo(() => {
        let resultado = faqs;
        if (categoriaActiva !== 'TODAS') {
            resultado = resultado.filter(f => f.category === categoriaActiva);
        }
        if (terminoBusqueda) {
            const termino = terminoBusqueda.toLowerCase();
            resultado = resultado.filter(f =>
                f.question.toLowerCase().includes(termino) ||
                f.answer.toLowerCase().includes(termino)
            );
        }
        return resultado;
    }, [terminoBusqueda, categoriaActiva]);

    const categorias = ['TODAS', ...Object.values(FAQCategory)] as const;

    const alternarFaq = (id: string) => {
        setFaqAbierta(faqAbierta === id ? null : id);
    };

    // Llegar con /ayuda#<id>: se quitan el filtro y la búsqueda para que la pregunta sea visible y se abre.
    const mostrarFaq = useCallback((id: string) => {
        setCategoriaActiva('TODAS');
        setTerminoBusqueda('');
        setFaqAbierta(id);
    }, []);
    useFaqDesdeHash(IDS_FAQ, mostrarFaq);

    return (
        <div className="py-24 min-h-screen relative">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-accent-blue-light border-2 border-ink px-4 py-1.5 rounded-full mb-6 shadow-[2px_2px_0px_0px_var(--shadow-color)]">
                        <HelpCircle className="w-4 h-4" />
                        <span>Soporte & FAQ</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-ink tracking-tight mb-4">
                        Centro de{' '}
                        <span className="bg-accent-blue-light px-4 py-1 rounded-lg border-2 border-ink inline-block shadow-[4px_4px_0px_0px_var(--shadow-color)] transform -rotate-1">
                            Ayuda
                        </span>
                    </h1>
                    <p className="text-lg text-ink/70 mb-10 max-w-2xl mx-auto font-medium">
                        Encuentra respuestas a las dudas más comunes. Si no encuentras lo que buscas, nuestro asistente de IA está listo para ayudarte.
                    </p>

                    {/* Buscador */}
                    <div className="relative max-w-xl mx-auto">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-5">
                            <Search className="h-5 w-5 text-ink/40" />
                        </div>
                        <input
                            type="text"
                            className="block w-full rounded-full border-2 border-ink py-4 pl-14 pr-6 text-ink bg-surface-2 placeholder:text-ink/40 focus:ring-2 focus:ring-accent-blue-medium focus:border-ink text-lg font-medium shadow-[4px_4px_0px_0px_var(--shadow-color)] transition-shadow focus:shadow-[6px_6px_0px_0px_var(--shadow-color)]"
                            placeholder="Buscar en preguntas frecuentes..."
                            value={terminoBusqueda}
                            onChange={(e) => setTerminoBusqueda(e.target.value)}
                        />
                    </div>
                </div>

                <ListaArticulosAyuda articulos={GUIAS_DE_USO} />

                <h2 className="text-2xl md:text-3xl font-bold font-display text-ink mb-6 text-center">Preguntas frecuentes</h2>

                {/* Filtros de categoría */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {categorias.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setCategoriaActiva(cat as FAQCategory | 'TODAS')}
                            className={`px-5 py-2.5 rounded-full text-sm font-bold font-display transition-all duration-200 border-2 border-ink ${categoriaActiva === cat
                                ? 'bg-ink text-canvas shadow-[2px_2px_0px_0px_var(--shadow-color)]'
                                : 'bg-surface-2 text-ink hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_var(--shadow-color)] shadow-[2px_2px_0px_0px_var(--shadow-color)]'
                                }`}
                        >
                            {cat === 'TODAS' ? '📋 Todas' : ETIQUETAS_CATEGORIA[cat as FAQCategory]}
                        </button>
                    ))}
                </div>

                {/* Lista de FAQ en acordeón */}
                <div className="space-y-4">
                    {faqsFiltradas.length === 0 ? (
                        <div className="text-center py-16 bg-surface-2 border-2 border-ink rounded-3xl shadow-[4px_4px_0px_0px_var(--shadow-color)]">
                            <p className="text-ink font-bold text-lg font-display">No hemos encontrado respuestas para &quot;{terminoBusqueda}&quot;</p>
                            <p className="text-ink/60 mt-2 font-medium">Prueba a usar el asistente chat abajo a la derecha. 👇</p>
                        </div>
                    ) : (
                        faqsFiltradas.map((faq) => (
                            <div
                                key={faq.id}
                                id={faq.id}
                                className={`scroll-mt-28 bg-surface-2 border-2 border-ink rounded-2xl overflow-hidden transition-all duration-200 ${faqAbierta === faq.id
                                    ? 'shadow-[6px_6px_0px_0px_var(--shadow-color)]'
                                    : 'shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:shadow-[6px_6px_0px_0px_var(--shadow-color)] hover:-translate-y-0.5'
                                    }`}
                            >
                                <button
                                    onClick={() => alternarFaq(faq.id)}
                                    aria-expanded={faqAbierta === faq.id}
                                    className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none group"
                                >
                                    <span className="text-lg font-bold font-display text-ink pr-8 group-hover:text-accent-blue-medium transition-colors">
                                        {faq.question}
                                    </span>
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-ink flex items-center justify-center bg-accent-blue-light text-ink">
                                        {faqAbierta === faq.id ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                    </span>
                                </button>

                                {faqAbierta === faq.id && (
                                    <div className="px-6 pb-6 pt-0">
                                        <div className="border-t-2 border-ink/10 pt-4">
                                            <p className="text-ink/80 text-base leading-relaxed font-medium">
                                                {faq.answer}
                                            </p>
                                            <div className="mt-4 flex flex-wrap items-center gap-4">
                                                <span className="inline-flex items-center rounded-full bg-accent-blue-light px-3 py-1 text-xs font-bold text-ink border border-ink/20">
                                                    {ETIQUETAS_CATEGORIA[faq.category]}
                                                </span>
                                                {SLUG_POR_ID_FAQ.has(faq.id) && (
                                                    <Link
                                                        href={`/ayuda/${SLUG_POR_ID_FAQ.get(faq.id)}`}
                                                        className="inline-flex min-h-11 items-center text-sm font-bold font-display text-ink underline underline-offset-4"
                                                    >
                                                        Abrir esta respuesta en su página
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* CTA de contacto */}
                <div className="mt-20 text-center bg-tint-accent rounded-3xl p-10 border-2 border-ink shadow-[8px_8px_0px_0px_var(--shadow-color)] relative overflow-hidden group">
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-accent-blue/30 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" />

                    <h3 className="text-2xl md:text-3xl font-bold font-display text-ink mb-3 relative z-10">
                        ¿Aún tienes dudas?
                    </h3>
                    <p className="text-ink/70 mb-8 font-medium relative z-10 max-w-lg mx-auto">
                        Usa nuestro chat interactivo en la esquina, o si prefieres, escríbenos a nuestro correo de soporte técnico.
                    </p>
                    <a
                        href="mailto:info@therapyrecap.com"
                        className="relative z-10 inline-flex items-center gap-3 bg-accent-blue text-on-accent-blue py-4 px-8 rounded-full text-lg font-bold font-display transition-all duration-300 shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:shadow-[6px_6px_0px_0px_var(--shadow-color)] hover:-translate-y-1 border-2 border-ink"
                    >
                        <Mail className="w-5 h-5" />
                        Contactar a Soporte
                    </a>
                </div>
            </div>
        </div>
    );
};
